import { Divider, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { IVendorQuote } from 'quotes/types';
import { Color, Level, Variant } from 'theme/enums';

import { BlockedQuoteList } from './BlockedQuoteList';

interface IProps {
  blockers: IVendorQuote[];
  omits: IVendorQuote[];
  eligibleCount: number;
  onExport: () => void;
  onCancel: () => void;
}

export const BulkExportSkuPartsWarning = ({
  blockers,
  omits,
  eligibleCount,
  onExport,
  onCancel,
}: IProps) => {
  const { t } = useTranslation();

  const message = (
    <Stack gap={2}>
      {blockers.length > 0 && (
        <Stack gap={0}>
          <Typography color={Color.DANGER} level={Level.TITLE_MD}>
            {t('titleBulkExportSkuPartsBlockers')} ({blockers.length})
          </Typography>
          <Typography level={Level.BODY_SM}>{t('messageBulkExportSkuPartsBlockers')}</Typography>
          <BlockedQuoteList quotes={blockers} withReason />
        </Stack>
      )}

      {omits.length > 0 && (
        <>
          {blockers.length > 0 && <Divider />}

          <Stack gap={0}>
            <Typography color={Color.WARNING} level={Level.TITLE_MD}>
              {t('titleBulkExportSkuPartsOmits')} ({omits.length})
            </Typography>
            <Typography level={Level.BODY_SM}>{t('messageBulkExportSkuPartsOmits')}</Typography>
            <BlockedQuoteList quotes={omits} />
          </Stack>
        </>
      )}
    </Stack>
  );

  // Export action lives in extraButtons: only that slot forwards `disabled` (onConfirm /
  // confirmationActions Buttons in Confirmation do not), so it's the only way to keep the
  // primary action disabled while blockers remain.
  const extraButtons = [
    <Button
      key="bulkExportSkuParts"
      text={t('buttonLabelBulkExportSkuParts', { count: eligibleCount })}
      variant={Variant.SOLID}
      color={Color.PRIMARY}
      disabled={blockers.length > 0}
      onClick={onExport}
    />,
  ];

  return (
    <Confirmation
      isOpen={true}
      title={t('titleBulkExportSkuParts')}
      message={message}
      onCancel={onCancel}
      color={blockers.length > 0 ? Color.DANGER : Color.WARNING}
      extraButtons={extraButtons}
      dialogSx={{ maxWidth: 600 }}
    />
  );
};
